import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum } from "@/components/ui-bits";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return ""; }
}

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/news")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO
        title="News"
        description="Firm news, announcements, and press mentions from Shelton Law PLLC."
        path="/news"
      />

      <section className="border-b border-hairline" data-testid="news-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <Eyebrow>News</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">Announcements &amp; press.</h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            Lateral additions, awards, speaking engagements, and media coverage. For long-form legal analysis, see the firm&apos;s Insights.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20" data-testid="news-list">
        {loading && <p className="text-ink-soft" data-testid="news-loading">Loading…</p>}

        {!loading && items.length === 0 && (
          <div className="border-t border-hairline pt-10" data-testid="news-empty">
            <p className="eyebrow">Forthcoming</p>
            <p className="font-serif text-3xl text-ink mt-4 leading-snug max-w-2xl">
              Firm news is added here as it happens.
            </p>
            <p className="text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl mt-5">
              Announcements, awards, and press coverage are published here. In the meantime, the People page introduces the firm&apos;s attorneys.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link
                to="/people"
                className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold"
                data-testid="news-empty-people-link"
              >
                People <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="border-t border-hairline">
            {items.map((n, i) => (
              <li key={n.id} className="border-b border-hairline">
                <Link
                  to={`/news/${n.slug}`}
                  className="group grid grid-cols-12 gap-6 py-8 lg:py-10 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
                  data-testid={`news-card-${n.slug}`}
                >
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-3">
                    {String(i + 1).padStart(2, "0")}
                  </SectionNum>
                  <div className="col-span-10 sm:col-span-8">
                    <p className="eyebrow">{formatDate(n.created_at)}</p>
                    <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight mt-3 group-hover:text-brass transition-colors">{n.title}</h3>
                    <p className="text-ink-soft text-sm leading-relaxed mt-3 max-w-2xl">{n.excerpt}</p>
                  </div>
                  <div className="hidden sm:flex col-span-3 justify-end items-baseline">
                    <ArrowUpRight className="w-4 h-4 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
}
